import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

/**
 * Query client tuned for a dashboard of many independent widgets.
 *
 * The defaults matter more than usual here: 34 widgets mounting at once with
 * naive settings produces 34 requests on every window focus, which on an ERP
 * means a thundering herd against the reporting queries every time someone
 * alt-tabs back.
 */
const client = new QueryClient({
  defaultOptions: {
    queries: {
      // Business figures do not change second to second, and a widget that
      // refetches on every remount makes dragging the grid feel expensive.
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      // The killer default. Off.
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: (failureCount, error: any) => {
        const status = error?.response?.status;
        // 401/403/404 are answers, not failures — retrying them just delays
        // the error state and burns requests.
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
  },
});

export const QueryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <QueryClientProvider client={client}>{children}</QueryClientProvider>
);

export { client as queryClient };
export default QueryProvider;
